import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { matchPath, useHistory, useLocation } from 'react-router';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Button, Layout, Menu } from 'antd';

import { SidebarNavigationItemInterface } from 'interfaces/navigationItem.interface';

import Colors from 'const/colors';

const { Sider } = Layout;

export const SIDEBAR_WIDTH_COLLAPSED = 90;
export const SIDEBAR_WIDTH_EXPANDED = 240;

function isItemActive(
  item: SidebarNavigationItemInterface,
  pathname: string,
): boolean {
  return item.matchPaths.some((path) =>
    Boolean(
      matchPath(pathname, {
        path: path === '/' ? '/' : `${path}*`,
        exact: true,
      }),
    ),
  );
}

interface SidebarProps {
  items: SidebarNavigationItemInterface[];
  collapsed: boolean;
  onToggle: () => void;
  /** Décalage haut, pour rester sous le header (et le bandeau d'impersonation). */
  topOffset: number;
}

const Sidebar: React.FC<SidebarProps> = (props) => {
  const { items, collapsed, onToggle, topOffset } = props;
  const { t } = useTranslation();
  const history = useHistory();
  // `useLocation` et non `history.location` : seul le hook déclenche un rendu
  // quand la route change, sans dépendre du remontage du layout.
  const { pathname } = useLocation();

  const selectedKeys = useMemo(
    () =>
      items.filter((item) => isItemActive(item, pathname)).map((i) => i.key),
    [items, pathname],
  );

  const menuItems = useMemo(
    () =>
      items.map(({ key, labelKey, icon }) => ({
        key,
        icon,
        label: t(labelKey),
      })),
    [items, t],
  );

  const handleClick = useCallback(
    ({ key }: { key: string }) => {
      const item = items.find((candidate) => candidate.key === key);
      if (item) {
        history.push(item.path);
      }
    },
    [history, items],
  );

  return (
    <Sider
      theme="light"
      trigger={null}
      collapsed={collapsed}
      collapsedWidth={SIDEBAR_WIDTH_COLLAPSED}
      width={SIDEBAR_WIDTH_EXPANDED}
      className="print:hidden"
      style={{
        position: 'fixed',
        top: topOffset,
        bottom: 0,
        left: 0,
        zIndex: 999,
        borderRight: `1px solid ${Colors.BeigeHellio}`,
        overflow: 'auto',
      }}
    >
      <Menu
        mode="inline"
        inlineCollapsed={collapsed}
        selectedKeys={selectedKeys}
        items={menuItems}
        onClick={handleClick}
        className="app-sidebar-menu"
        style={{ borderInlineEnd: 'none', paddingTop: 12 }}
      />
      <Button
        type="primary"
        icon={collapsed ? <RightOutlined /> : <LeftOutlined />}
        onClick={onToggle}
        aria-expanded={!collapsed}
        aria-label={t(
          collapsed ? 'navigation:expand_menu' : 'navigation:collapse_menu',
        )}
        className="app-sidebar-toggle !absolute bottom-8 right-0 !h-8 !w-8 !min-w-0 !rounded-l-lg !rounded-r-none !p-0"
      />
    </Sider>
  );
};

export default React.memo(Sidebar);
